import {
  CONTAINER_FIELD_REGISTRY,
  registryColumnsForRole,
} from './container-field-registry.mjs?v=20260806.100';

export const ADAPTIVE_COLUMN_REGISTRY = CONTAINER_FIELD_REGISTRY;

const PRIORITIES_BY_TIER = Object.freeze({
  mobile: new Set(['P0']),
  compact: new Set(['P0', 'P1']),
  standard: new Set(['P0', 'P1', 'P2']),
  wide: new Set(['P0', 'P1', 'P2', 'P3']),
  ultra: new Set(['P0', 'P1', 'P2', 'P3']),
});

export function workspaceTier(width) {
  const value = Number(width);
  if (value < 900) return 'mobile';
  if (value < 1200) return 'compact';
  if (value < 1600) return 'standard';
  if (value < 1920) return 'wide';
  return 'ultra';
}

// The table's columns, which are the role's columns minus the detail-only ones. The
// Task detail modal builds its sections from registryColumnsForRole directly, so a
// detail-only field is still reachable - it is just not given a column in a 410-row
// table (Alan, 2026-08-02, for DO and POD).
export function authorizedColumns(role) {
  return registryColumnsForRole(role).filter(({ detailOnly }) => detailOnly !== true);
}

export function buildColumnView({ role, tier, preferences = null }) {
  const available = authorizedColumns(role);
  if (tier !== 'mobile') {
    return Object.freeze({
      available: Object.freeze([...available]),
      visible: Object.freeze([...available]),
    });
  }
  const allowed = new Set(available.map(({ key }) => key));
  const requestedVisible = Array.isArray(preferences?.visible)
    ? preferences.visible.filter((key) => allowed.has(key)) : [];
  const requestedHidden = new Set(Array.isArray(preferences?.hidden)
    ? preferences.hidden.filter((key) => allowed.has(key)) : []);
  const defaults = available.filter((column) => PRIORITIES_BY_TIER[tier]?.has(column.priority));
  const selected = requestedVisible.length
    ? available.filter((column) => requestedVisible.includes(column.key))
    : defaults.filter((column) => !requestedHidden.has(column.key));
  const p0 = available.filter((column) => column.priority === 'P0');
  const visibleKeys = new Set([...p0, ...selected].map(({ key }) => key));
  return Object.freeze({
    available: Object.freeze([...available]),
    visible: Object.freeze(available.filter(({ key }) => visibleKeys.has(key))),
  });
}

export function createColumnPreferenceStore({ storage, key }) {
  // The product is AY TMS. `km-tms-` was the old brand and its stored state is from a build far
  // enough back that it is not worth carrying forward (Alan, 2026-07-31) — this is a rename, not
  // a migration. Anyone holding an old layout starts from defaults.
  const storageKey = `ay-tms-columns:${key}`;
  return Object.freeze({
    load(role) {
      try {
        const parsed = JSON.parse(storage?.getItem(storageKey) || 'null');
        const allowed = new Set(authorizedColumns(role).map(({ key: columnKey }) => columnKey));
        return {
          visible: Array.isArray(parsed?.visible) ? parsed.visible.filter((value) => allowed.has(value)) : [],
          hidden: Array.isArray(parsed?.hidden) ? parsed.hidden.filter((value) => allowed.has(value)) : [],
        };
      } catch (_) { return { visible: [], hidden: [] }; }
    },
    save(role, preferences) {
      const allowed = new Set(authorizedColumns(role).map(({ key: columnKey }) => columnKey));
      const safe = {
        visible: [...new Set(preferences.visible || [])].filter((value) => allowed.has(value)),
        hidden: [...new Set(preferences.hidden || [])].filter((value) => allowed.has(value)),
      };
      storage?.setItem(storageKey, JSON.stringify(safe));
      return safe;
    },
  });
}

export function mountWorkspaceTier({
  element,
  ResizeObserverClass = globalThis.ResizeObserver,
  onTierChange = () => {},
}) {
  if (!element || typeof ResizeObserverClass !== 'function') return Object.freeze({ destroy() {} });
  let current = null;
  const apply = (width) => {
    const next = workspaceTier(width);
    if (next === current) return;
    current = next;
    element.dataset.workspaceTier = next;
    onTierChange(next);
  };
  apply(element.getBoundingClientRect().width);
  const observer = new ResizeObserverClass((entries) => apply(entries[0]?.contentRect?.width || 0));
  observer.observe(element);
  return Object.freeze({ destroy() { observer.disconnect(); } });
}
